import React from "react";
import { useQuery, gql } from "@apollo/client";

// Change history of system settings and restricted / normal days: who
// changed what, when, from what to what. Filter by type and, optionally,
// one key (a config key or a date).

const GET_SETTINGS_CHANGE_LOG = gql`
  query GetSettingsChangeLog($entityType: String, $entityKey: String, $limit: Int) {
    getSettingsChangeLog(entityType: $entityType, entityKey: $entityKey, limit: $limit) {
      status
      message
      entries {
        id
        entityType
        entityKey
        action
        oldValue
        newValue
        changedAt
        changedByName
      }
    }
  }
`;

const ACTION_BADGE = {
  CREATED: "bg-green-100 text-green-800",
  UPDATED: "bg-blue-100 text-blue-800",
  DELETED: "bg-red-100 text-red-800",
};

const showValue = (entry, value) => {
  if (value === null || value === undefined) return "—";
  if (entry.entityType === "RESTRICTED_DAY") {
    const kind = value.kind === "NORMAL" ? "Normal weekend" : "Restricted";
    return value.name ? `${kind} · ${value.name}` : kind;
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const SettingsHistoryModal = ({ entityType, entityKey, title, onClose }) => {
  const { data, loading } = useQuery(GET_SETTINGS_CHANGE_LOG, {
    variables: { entityType, entityKey, limit: 200 },
    fetchPolicy: "network-only",
  });
  const entries = data?.getSettingsChangeLog?.entries || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">
            ×
          </button>
        </div>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-500">
            No changes recorded yet. Changes are recorded from 22 Oct 2026 onwards.
          </p>
        ) : (
          <table className="min-w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-gray-700 border-b">
              <tr>
                <th className="py-2 px-3">When</th>
                <th className="py-2 px-3">Who</th>
                {!entityKey && <th className="py-2 px-3">{entityType === "RESTRICTED_DAY" ? "Date" : "Setting"}</th>}
                <th className="py-2 px-3">Change</th>
                <th className="py-2 px-3">From</th>
                <th className="py-2 px-3">To</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="py-2 px-3 whitespace-nowrap">
                    {new Date(e.changedAt).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="py-2 px-3">{e.changedByName || "—"}</td>
                  {!entityKey && <td className="py-2 px-3 font-mono text-xs">{e.entityKey}</td>}
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ACTION_BADGE[e.action] || ""}`}>
                      {e.action.toLowerCase()}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs text-gray-500 max-w-[180px] break-words">
                    {showValue(e, e.oldValue)}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs text-gray-800 max-w-[180px] break-words">
                    {showValue(e, e.newValue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SettingsHistoryModal;
